import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Beer, Plus, Minus, ShoppingCart, Send, Clock, CheckCircle } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';

const STATUS_STYLES = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  preparing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  ready: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  delivered: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

const STATUS_LABELS = {
  pending: 'Venter',
  preparing: 'Forberedes',
  ready: 'Klar',
  delivered: 'Leveret',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
      {status === 'ready' || status === 'delivered' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function BarPage() {
  const [drinks, setDrinks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => {
    Promise.all([
      apiGet('/api/drinks').then((r) => setDrinks((r.data.drinks || []).filter((d) => d.active !== false))),
      apiGet('/api/drink-orders/me').then((r) => setOrders(r.data.orders || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    return drinks.reduce((acc, drink) => {
      const category = drink.category || 'Andet';
      if (!acc[category]) acc[category] = [];
      acc[category].push(drink);
      return acc;
    }, {});
  }, [drinks]);

  const cart = drinks
    .map((drink) => ({
      ...drink,
      quantity: quantities[drink.id] || 0,
    }))
    .filter((drink) => drink.quantity > 0);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const changeQty = (drinkId, delta) => {
    setQuantities((prev) => {
      const next = Math.max(0, (prev[drinkId] || 0) + delta);
      return { ...prev, [drinkId]: next };
    });
  };

  const submitOrder = async () => {
    if (cart.length === 0) return;

    setSending(true);
    try {
      await apiPost('/api/drink-orders', {
        items: cart.map((item) => ({
          drink_id: item.id,
          name: item.name,
          category: item.category,
          quantity: item.quantity,
        })),
      });

      setQuantities({});
      alert('Bestilling sendt til baren.');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke sende bestilling');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto animate-fade-in" data-testid="bar-page">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-[0.2em] mb-4">
          <Beer className="w-3.5 h-3.5" />
          Molin30 Bar
        </div>

        <h1 className="text-3xl font-semibold tracking-tight text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Barbestilling
        </h1>

        <p className="text-sm text-slate-400 max-w-2xl">
          Bestil drikkevarer direkte fra portalen. Din bestilling sendes til baren.
        </p>
      </div>

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400">Seneste bestillinger</h2>
          </div>
          <span className="text-xs text-slate-500">Opdateres hvert 5. sekund</span>
        </div>

        {orders.length === 0 ? (
          <p className="text-sm text-slate-500">Ingen barbestillinger endnu.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {orders.slice(0, 6).map((order) => (
              <div key={order.id} className="bg-slate-900/40 border border-slate-700/40 rounded-lg p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <StatusBadge status={order.status} />
                  <span className="text-xs text-slate-500 text-right">
                    <span className="block">{formatRelativeTime(order.created_at)}</span>
                    <span className="block text-slate-600">{order.created_at ? new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </span>
                </div>
                <div className="space-y-1">
                  {order.items?.map((item, idx) => (
                    <p key={idx} className="text-sm text-slate-300">
                      {item.quantity}x {item.name}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {loading ? (
            <p className="text-sm text-slate-400">Indlæser...</p>
          ) : drinks.length === 0 ? (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-8 text-center">
              <Beer className="w-12 h-12 text-amber-400/40 mx-auto mb-3" />
              <p className="text-sm text-slate-400">Barmenuen er ikke klar endnu.</p>
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">
                  {category}
                </h2>

                <div className="space-y-3">
                  {items.map((drink) => {
                    const qty = quantities[drink.id] || 0;

                    return (
                      <div
                        key={drink.id}
                        className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                      >
                        <div className="w-full min-w-0">
                          <h3 className="text-slate-100 font-medium">{drink.name}</h3>
                          {drink.description && (
                            <p className="text-sm text-slate-400 mt-1">{drink.description}</p>
                          )}
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
                          <button
                            onClick={() => changeQty(drink.id, -1)}
                            className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-700 disabled:opacity-40"
                            disabled={qty === 0}
                          >
                            <Minus className="w-4 h-4 mx-auto" />
                          </button>

                          <span className="w-6 text-center text-slate-100 font-semibold">{qty}</span>

                          <button
                            onClick={() => changeQty(drink.id, 1)}
                            className="w-9 h-9 rounded-lg bg-blue-600 text-white hover:bg-blue-500"
                          >
                            <Plus className="w-4 h-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 h-fit sticky top-24">
          <div className="flex items-center gap-2 mb-5">
            <ShoppingCart className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-slate-100">Din bestilling</h2>
          </div>

          {cart.length === 0 ? (
            <p className="text-sm text-slate-500">Vælg drikkevarer for at oprette en bestilling.</p>
          ) : (
            <div className="space-y-3 mb-6">
              {cart.map((item) => (
                <div key={item.id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="min-w-0 break-words text-slate-300">{item.quantity}x {item.name}</span>
                  <span className="flex-shrink-0 text-slate-500">{item.category}</span>
                </div>
              ))}
            </div>
          )}

          <Button
            className="w-full bg-amber-600 hover:bg-amber-500 text-white"
            disabled={cart.length === 0 || sending}
            onClick={submitOrder}
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sender...' : `Send bestilling${totalItems ? ` (${totalItems})` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
